import {
  abnahmeMeldungBeschreiben,
  ERINNERUNGS_TAKTE,
  ERINNERUNG_STANDARD_SOFORT,
  ERINNERUNG_STANDARD_TAG,
  ERINNERUNG_STANDARD_TAKT,
  ERINNERUNGS_TAKT_LABELS,
  WOCHENTAGE,
  VORWARN_STANDARD_TAGE,
  VORWARN_TAGE_MAX,
  VORWARN_TAGE_OPTIONEN,
  vorwarnungBeschreiben,
  vorwarnZeitText,
  type ErinnerungsTakt,
  type Vorwarneinstellung,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { meineErinnerungQuery, meineVorwarnungQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'

/**
 * **Wie oft mich Work an offene Abnahmen erinnert**
 * ([Z-09](../../../../docs/phasen/zusatz-09-abnahme-erinnerung.md)).
 *
 * Nils, 25.08.2026: *„Die Häufigkeit sollte man einstellen können (am besten pro Benutzer jeder
 * selber), Standard 1x mittwochs."*
 *
 * ## Warum das unten an der eigenen Person sitzt und nicht in „Einstellungen"
 *
 * Weil „Einstellungen" die **Administrationsansicht** ist – Zugänge, Rollen, Regeln, KI. Wer
 * dort eine persönliche Angabe hinstellt, versteckt sie vor genau der Hälfte der Leute, für die
 * sie gedacht ist: der Mit-Owner sieht diese Ansicht nie und ist trotzdem der typische
 * Abnehmer.
 *
 * Unten links steht der eigene Name, die eigene Rolle und der Abmeldeknopf. Das ist der Ort,
 * an dem „was mich betrifft" schon steht – die Erinnerung gehört daneben und nirgendwo sonst.
 *
 * ## Warum ein Aufklapper und keine dauerhafte Zeile
 *
 * Weil sie zweimal im Jahr angefasst wird. Eine Zeile, die immer sichtbar ist und fast nie
 * benutzt wird, kostet jede Seitenleiste Platz – dieselbe Überlegung, aus der es in Z-03 keinen
 * Pin-Knopf in 700 Listenzeilen gibt.
 */
export function ErinnerungsWahl() {
  const [offen, setOffen] = useState(false)
  const queryClient = useQueryClient()

  const { data } = useQuery(meineErinnerungQuery)

  /**
   * **Optimistisch wie alles andere** (Leitplanke 1): die Zeile springt sofort um. Man liest
   * hier eine Zahl, die man gerade selbst angeklickt hat – auf eine Serverantwort dafür zu
   * warten wäre der Spinner, den es nicht geben darf.
   */
  const setzen = useMutation({
    mutationFn: (wert: { takt: ErinnerungsTakt; wochentag: number; sofort: boolean }) =>
      trpc.people.erinnerungSetzen.mutate(wert),
    onMutate: async (wert) => {
      await queryClient.cancelQueries({ queryKey: meineErinnerungQuery.queryKey })
      const vorher = queryClient.getQueryData(meineErinnerungQuery.queryKey)
      queryClient.setQueryData(meineErinnerungQuery.queryKey, wert)
      return { vorher }
    },
    onError: (_fehler, _wert, kontext) => {
      queryClient.setQueryData(meineErinnerungQuery.queryKey, kontext?.vorher)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: meineErinnerungQuery.queryKey }),
  })

  const takt = (data?.takt ?? ERINNERUNG_STANDARD_TAKT) as ErinnerungsTakt
  const wochentag = data?.wochentag ?? ERINNERUNG_STANDARD_TAG
  const sofort = data?.sofort ?? ERINNERUNG_STANDARD_SOFORT

  /**
   * **`??` und nicht `||`** – bei `sofort` ist `false` eine Aussage. Mit `||` ließe sich der
   * Schalter nie abschalten: das ausdrückliche `false` fiele auf den alten Wert zurück.
   */
  const speichern = (neu: {
    takt?: ErinnerungsTakt
    wochentag?: number
    sofort?: boolean
  }): void => {
    setzen.mutate({
      takt: neu.takt ?? takt,
      wochentag: neu.wochentag ?? wochentag,
      sofort: neu.sofort ?? sofort,
    })
  }

  return (
    <div className="px-2 pb-1">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="w-full cursor-pointer rounded-lg px-1 py-1 text-left text-[11px] text-muted hover:text-ink2"
      >
        🔔 Abnahme-Erinnerung: {abnahmeMeldungBeschreiben({ takt, wochentag }, sofort)}
        <span className="ml-1">{offen ? '▾' : '▸'}</span>
      </button>

      {offen && (
        <div className="mt-1 rounded-lg border border-line2 bg-page p-2">
          {/**
           * **Der Sofort-Schalter steht über dem Takt** ([E-302](../../../../docs/ENTSCHEIDUNGEN.md#e-302)).
           *
           * Nils, 02.09.2026: *„Und die Abnahmen doch verschicken sobald die auftauchen (Die
           * wollen schneller abnehmen)."*
           *
           * Oben, weil er die häufigere Antwort ist – und als Häkchen und nicht als vierter
           * Takt-Knopf, weil er eine andere Frage beantwortet: der Takt sagt, wann die **Liste
           * des Offenen** kommt, das Häkchen, ob man **Neues** sofort hört. Nebeneinander in
           * einer Reihe hieße „entweder – oder", und wer „sofort" wählte, verlöre lautlos die
           * Zusammenfassung.
           */}
          <label className="flex cursor-pointer items-start gap-1.5 text-[11px] text-ink2">
            <input
              type="checkbox"
              checked={sofort}
              onChange={(e) => speichern({ sofort: e.target.checked })}
              className="mt-[2px] size-[12px] cursor-pointer accent-accent"
            />
            <span>
              Sofort melden, sobald etwas Neues dazukommt
              <span className="block text-[10.5px] text-muted">
                Höchstens eine Mail alle fünf Minuten – mehrere Aufgaben kommen gebündelt.
              </span>
            </span>
          </label>

          <div className="mt-2 border-t border-line2 pt-2 text-[10.5px] text-muted">
            Und zusätzlich die Liste dessen, was offen ist:
          </div>

          <div
            role="radiogroup"
            aria-label="Wie oft erinnern?"
            className="mt-1 flex flex-wrap gap-1"
          >
            {ERINNERUNGS_TAKTE.map((wert) => (
              <button
                key={wert}
                type="button"
                role="radio"
                aria-checked={takt === wert}
                onClick={() => speichern({ takt: wert })}
                className={`cursor-pointer rounded-md border px-2 py-[2px] text-[11px] ${
                  takt === wert
                    ? 'border-accent bg-accent-soft text-accent-deep'
                    : 'border-line2 bg-surface text-ink2 hover:bg-page'
                }`}
              >
                {ERINNERUNGS_TAKT_LABELS[wert]}
              </button>
            ))}
          </div>

          {/* Der Wochentag hat nur bei „wöchentlich" eine Bedeutung – sonst wäre er eine
              Stellschraube ohne Wirkung, und die ist schlimmer als keine. */}
          {takt === 'woechentlich' && (
            <div
              role="radiogroup"
              aria-label="An welchem Wochentag?"
              className="mt-1.5 flex flex-wrap gap-1"
            >
              {WOCHENTAGE.map((tag) => (
                <button
                  key={tag.wert}
                  type="button"
                  role="radio"
                  aria-checked={wochentag === tag.wert}
                  aria-label={tag.lang}
                  onClick={() => speichern({ wochentag: tag.wert })}
                  className={`w-7 cursor-pointer rounded-md border py-[2px] text-[11px] ${
                    wochentag === tag.wert
                      ? 'border-accent bg-accent-soft text-accent-deep'
                      : 'border-line2 bg-surface text-ink2 hover:bg-page'
                  }`}
                >
                  {tag.kurz}
                </button>
              ))}
            </div>
          )}

          <p className="mt-1.5 text-[10.5px] text-muted">
            Die Mail kommt nur, wenn wirklich etwas auf deine Abnahme wartet.
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * **Wie lange vorher mich Work an Termine erinnert**
 * ([Z-13](../../../../docs/phasen/zusatz-13-frist-vorwarnung.md)).
 *
 * Nils, 28.08.2026: *„Bei Fälligkeit möchte ich auch einstellen ob ich x Tage vorher erinnert
 * werden will per Mail und per System."*
 *
 * ## Warum hier und nicht in „Einstellungen"
 *
 * Derselbe Grund wie eine Komponente weiter oben: „Einstellungen" ist die
 * Administrationsansicht, und der Mit-Owner sieht sie nie. Was mich betrifft, steht dort, wo
 * mein Name steht.
 *
 * ## Warum eine zweite Zeile und nicht ein Block mit der Abnahme-Erinnerung
 *
 * Weil es zwei verschiedene Fragen sind und man sie an ihrer Antwort auseinanderhält: die eine
 * ist ein **Takt** („einmal mittwochs"), die andere ein **Wecker** („drei Tage vor dem
 * Termin"). Zusammengelegt stünde dort eine Liste aus vier Stellschrauben, bei der man vor
 * jeder Änderung erst lesen muss, worauf sie wirkt.
 */
export function VorwarnWahl() {
  const [offen, setOffen] = useState(false)
  const queryClient = useQueryClient()

  const { data } = useQuery(meineVorwarnungQuery)

  const setzen = useMutation({
    mutationFn: (wert: Vorwarneinstellung) => trpc.vorwarnung.einstellungSetzen.mutate(wert),
    onMutate: async (wert) => {
      await queryClient.cancelQueries({ queryKey: meineVorwarnungQuery.queryKey })
      const vorher = queryClient.getQueryData(meineVorwarnungQuery.queryKey)
      queryClient.setQueryData(meineVorwarnungQuery.queryKey, (alt) =>
        alt === undefined ? alt : { ...alt, einstellung: wert },
      )
      return { vorher }
    },
    onError: (_fehler, _wert, kontext) => {
      queryClient.setQueryData(meineVorwarnungQuery.queryKey, kontext?.vorher)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: meineVorwarnungQuery.queryKey }),
  })

  const einstellung: Vorwarneinstellung = data?.einstellung ?? {
    tage: VORWARN_STANDARD_TAGE,
    perMail: true,
    imSystem: true,
  }

  const speichern = (neu: Partial<Vorwarneinstellung>): void => {
    setzen.mutate({ ...einstellung, ...neu })
  }

  /** Wie viele Aufgaben davon betroffen sind – sonst ist die Einstellung eine Zahl ohne Bezug. */
  const anzahl = data?.aufgaben.length ?? 0

  /**
   * **Steht die eingestellte Zahl auf keinem Knopf, gehört sie ins Feld** – und das Feld ist
   * dann das Hervorgehobene. Sonst sähe eine Einstellung von 30 Tagen aus, als sei nichts
   * gewählt.
   */
  const eigeneGewaehlt = !(VORWARN_TAGE_OPTIONEN as readonly number[]).includes(einstellung.tage)

  /**
   * **`null` heißt „gerade tippt niemand"** – dann zeigt das Feld, was gilt.
   *
   * Der Zwischenspeicher hält nur den **Entwurf** während der Eingabe. Läge dort dauerhaft
   * eine Zeichenkette, liefe sie der Einstellung hinterher: die Antwort des Servers kommt nach
   * dem ersten Rendern, und ein Klick auf einen Knopf ließe die alte Zahl im Feld stehen.
   */
  const [entwurf, setEntwurf] = useState<string | null>(null)
  const eigene = entwurf ?? (eigeneGewaehlt ? String(einstellung.tage) : '')

  const uebernehmen = (): void => {
    if (entwurf === null) return
    const zahl = Number(entwurf)
    /** Ungültiges wird verworfen, nicht gerundet – dazu genügt es, den Entwurf fallen zu lassen. */
    setEntwurf(null)
    if (entwurf.trim() === '' || !Number.isInteger(zahl) || zahl < 0 || zahl > VORWARN_TAGE_MAX) {
      return
    }
    if (zahl !== einstellung.tage) speichern({ tage: zahl })
  }

  return (
    <div className="px-2 pb-1">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="w-full cursor-pointer rounded-lg px-1 py-1 text-left text-[11px] text-muted hover:text-ink2"
      >
        {/**
         * **Ein Name, nicht zwei** ([E-324](../../../../docs/ENTSCHEIDUNGEN.md#e-324),
         * 04.09.2026).
         *
         * Nils: *„verstehe gar nicht was Vorwarnung ist? Brauchen wir das, wenn wir das Erinnern
         * haben? Ist doch doppelt oder nicht?"*
         *
         * Doppelt war nur das Wort: „Vorwarnung" hier unten und „🔔 Erinnern" am Chip waren
         * immer dasselbe. Zwei Namen für eine Sache sind der Grund, warum er sie nie
         * wiedererkannt hat – seine Einstellung stand nach zwei Wochen noch auf dem
         * Standardwert.
         *
         * **„standardmäßig"** steht seit E-324 dabei: die Zahl ist jetzt die Voreinstellung,
         * die Wahl je Aufgabe sitzt am Chip.
         */}
        🔔 Erinnern an Termine: standardmäßig {vorwarnungBeschreiben(einstellung).toLowerCase()}
        <span className="ml-1">{offen ? '▾' : '▸'}</span>
      </button>

      {offen && (
        <div className="mt-1 rounded-lg border border-line2 bg-page p-2">
          <div className="flex flex-wrap items-center gap-1">
            {/**
             * **Die Knöpfe bleiben unter sich.** Das freie Feld steht daneben und **außerhalb**
             * der Gruppe: ein Eingabefeld in einer `radiogroup` ist für Vorleseprogramme ein
             * Element, das dort nicht vorkommen darf – es würde als Auswahlmöglichkeit
             * angesagt, die man nicht auswählen kann.
             */}
            <div
              role="radiogroup"
              aria-label="Wie lange vorher – standardmäßig?"
              className="flex flex-wrap gap-1"
            >
              {VORWARN_TAGE_OPTIONEN.map((wert) => (
                <button
                  key={wert}
                  type="button"
                  role="radio"
                  aria-checked={einstellung.tage === wert}
                  aria-label={vorwarnZeitText(wert)}
                  onClick={() => {
                    /** Ein Klick auf einen Knopf beendet die Eingabe – sonst blieben zwei Zahlen. */
                    setEntwurf(null)
                    speichern({ tage: wert })
                  }}
                  className={`cursor-pointer rounded-md border px-2 py-[2px] text-[11px] ${
                    einstellung.tage === wert
                      ? 'border-accent bg-accent-soft text-accent-deep'
                      : 'border-line2 bg-surface text-ink2 hover:bg-page'
                  }`}
                >
                  {wert === 0 ? 'am Tag' : `${wert} T`}
                </button>
              ))}
            </div>

            {/**
             * **Das freie Feld neben den Knöpfen** ([E-303](../../../../docs/ENTSCHEIDUNGEN.md#e-303)).
             *
             * Nils, 02.09.2026: *„da sollte ich 1, 3, 5, 10 Tage auswählen können (oder
             * flexibel eingebbar 1–99)"*. Also **beides**: die Knöpfe für die Zahlen, die man
             * ohne Nachdenken nimmt, das Feld für die, die man begründet.
             *
             * **Gespeichert wird erst beim Verlassen des Feldes** (`onBlur`), nicht bei jedem
             * Tastendruck: wer „10" tippt, hätte sonst zwischendurch „1" gespeichert – und
             * jede Ziffer wäre ein Serveraufruf. Die Eingabetaste tut dasselbe, weil man das
             * dort erwartet.
             *
             * Ungültiges wird **verworfen, nicht gerundet**: `Math.min(99, …)` machte aus einem
             * vertippten „400" ein stillschweigendes „99", und die Zahl im Feld hätte niemand
             * gewählt.
             */}
            <label className="flex items-center gap-1 text-[11px] text-muted">
              <span>oder</span>
              <input
                type="number"
                min={0}
                max={VORWARN_TAGE_MAX}
                value={eigene}
                aria-label={`Eigene Vorwarnzeit in Tagen, 0 bis ${VORWARN_TAGE_MAX}`}
                onChange={(e) => setEntwurf(e.target.value)}
                onBlur={() => uebernehmen()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur()
                }}
                className={`w-[46px] rounded-md border px-1 py-[2px] text-[11px] ${
                  eigeneGewaehlt
                    ? 'border-accent bg-accent-soft text-accent-deep'
                    : 'border-line2 bg-surface text-ink2'
                }`}
              />
              <span>Tage</span>
            </label>
          </div>

          <div className="mt-1.5 flex flex-wrap gap-2">
            <label className="flex cursor-pointer items-center gap-1 text-[11px] text-ink2">
              <input
                type="checkbox"
                checked={einstellung.perMail}
                onChange={(e) => speichern({ perMail: e.target.checked })}
                className="size-[12px] cursor-pointer accent-accent"
              />
              per Mail
            </label>
            <label className="flex cursor-pointer items-center gap-1 text-[11px] text-ink2">
              <input
                type="checkbox"
                checked={einstellung.imSystem}
                onChange={(e) => speichern({ imSystem: e.target.checked })}
                className="size-[12px] cursor-pointer accent-accent"
              />
              im System
            </label>
          </div>

          <p className="mt-1.5 text-[10.5px] text-muted">
            {anzahl === 0
              ? 'Gilt für Aufgaben, bei denen du „Benachrichtigen" anhakst.'
              : `Gilt für ${anzahl === 1 ? 'eine Aufgabe' : `${anzahl} Aufgaben`}, die du dir vorgemerkt hast.`}
          </p>
        </div>
      )}
    </div>
  )
}
